import { useEffect } from 'react';
import { Flex } from 'antd';
import { useTranslation } from 'react-i18next';
import { spacing } from '../../theming';
import { useVinExtractionService } from '../../services/vinExtractionService';
import { parseStats } from '../../utils/vinExtraction';
import { isLoadFailed, useVinRequest } from './useVinRequest';
import VinLoadError from './VinLoadError';
import StatsLoadingState from './StatsLoadingState';
import StatsBody, { PeriodHeader } from './StatsBody';

/**
 * Stats from one GET /stats: the vehicle database card, then the range picker (`controls`) and the period's cards,
 * "Success vs failed" and failure reasons. Its loading, error and empty states are its own; a failure here never
 * blanks the rest of the page, and the picker stays usable while it loads or fails.
 */
export default function StatsSection({ range, refreshToken, onFetched, controls, settings }) {
    const { t } = useTranslation('vinExtraction');
    const { getStats } = useVinExtractionService();
    const { load, run } = useVinRequest({
        request: () => getStats({ from: range.from, to: range.to, tz: range.tz }),
        parse: parseStats,
        onReady: onFetched,
    });

    useEffect(() => {
        run();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range, refreshToken]);

    return (
        <Flex
            vertical
            gap={spacing[3]}
            role="region"
            aria-label={t('stats.aria')}
            aria-busy={load.status === 'loading'}
        >
            {load.status !== 'ready' && <PeriodHeader controls={controls} />}
            {load.status === 'loading' && <StatsLoadingState />}
            {isLoadFailed(load) && (
                <VinLoadError
                    status={load.status}
                    error={load.error}
                    message={t('stats.loadFailed')}
                    invalidDescription={t('stats.invalidResponse')}
                    onRetry={run}
                />
            )}
            {load.status === 'ready' && (
                <StatsBody stats={load.data} settings={settings} controls={controls} onRetry={run} />
            )}
        </Flex>
    );
}
